<template>
  <div
    class="bg-white"
  >
    <div>
      <table class="table m-0 b-table b-table-fixed">
        <tr
          v-for="(campaign, index) in campaigns"
          :key="index"
        >
          <td
            ref="row-details-expand"
            class="row-details-expand"
          >
            <!-- Empty for Styling -->
          </td>
          <td
            ref="row-details-campaign-name"
            class="row-details-campaign-name text-wrap"
          >
            <span
              class="ellipsis"
              data-test="campaign-title"
            >
              <router-link
                v-if="isDraftState(campaign)"
                data-test="campaign-details-link"
                class="large"
                :to="{
                  name: 'NewCampaign',
                  params: {
                    campaignId: campaign.id,
                    advertiserId: campaign.advertiserId
                  }
                }"
              >
                {{ campaign.name }}
              </router-link>
              <router-link
                v-else-if="isEndedState(campaign)"
                class="large"
                data-test="campaign-view-link"
                :to="{
                  name: 'ViewCampaign',
                  params: {
                    campaignId: campaign.id,
                    advertiserId: campaign.advertiserId
                  }
                }"
              >
                {{ campaign.name }}
              </router-link>
              <feature
                name="CAMPAIGN_EDIT"
                v-else-if="canEdit(campaign)"
              >
                <router-link
                  class="large"
                  data-test="campaign-edit-link"
                  :to="{ name: 'EditCampaign',
                         params: {
                           campaignId: campaign.id,
                           advertiserId: campaign.advertiserId
                         }
                  }"
                >
                  {{ campaign.name }}
                </router-link>
              </feature>
            </span>
          </td>
          <td
            ref="row-details-state"
            class="row-details-state"
          >
            <div>
              <span
                v-if="campaign.state"
                :data-test="`campaign-status-${campaign.state}`"
              >
                <span :class="[getStatusIcon(campaign), getStatusColor(campaign)]" />
                {{ getColumnState(campaign) }}
              </span>
              <a
                v-if="campaign.state === 'LIVE' && isMyDashboard"
                href="#"
                class="icon ml-3"
                data-test="campaign-status-pause"
                @click="setCampaignLive(campaign, 'PAUSED')"
              >
                <i class="fa-light fa-pause" />
              </a>
              <a
                v-if="campaign.state === 'PAUSED' && isMyDashboard"
                href="#"
                class="icon ml-3"
                data-test="campaign-status-start"
                @click="setCampaignLive(campaign, 'LIVE')"
              >
                <i class="fa-light fa-play" />
              </a>
            </div>
          </td>
          <td
            ref="row-details-start-date"
            class="row-details-start-date"
          >
            <small>{{ formatDateForPlatform(campaign.startDate) }}</small>
          </td>
          <td
            ref="row-details-end-date"
            class="row-details-end-date"
          >
            <small>{{ formatDateForPlatform(campaign.endDate) }}</small>
          </td>
          <td
            ref="row-details-budget"
            class="row-details-budget"
          >
            <div>
              <div class="clearfix">
                <div
                  class="float-left"
                  data-test="budget-spent-percentage"
                >
                  <strong>{{ getBudgetSpentPercentage(campaign.budgetRemaining) || '0' }}%</strong>
                </div>
                <div class="float-right">
                  <small class="text-muted">{{ formattedMoneyAmount(campaign) }}</small>
                </div>
              </div>
              <div class="progress progress-xs">
                <div
                  class="progress-bar"
                  :class="setProgressColor(getBudgetSpentPercentage(campaign.budgetRemaining)
                    || 0)"
                  role="progressbar"
                  :style="`width: ${getBudgetSpentPercentage(campaign.budgetRemaining) || '0'}%`"
                  :aria-valuenow="getBudgetSpentPercentage(campaign.budgetRemaining) || '0'"
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>
          </td>
          <td
            ref="row-details-impressions"
            class="row-details-impressions"
          >
            {{ campaign.impressions }}
          </td>
          <td
            ref="row-details-clicks"
            class="row-details-clicks"
          >
            {{ campaign.clicks }}
          </td>
          <td
            ref="row-details-ctr"
            class="row-details-ctr"
          >
            {{ campaign.ctr > 0 ? `${campaign.ctr}%` : '%' }}
          </td>
          <td
            ref="row-details-account-manager"
            class="row-details-account-manager"
            v-if="dashboardName !== 'selfServiceDashboard' && isManaged"
          >
            <div
              class="avatar avatar-manager"
              :class="getAvatarClass(campaign.accountManagerUserId)"
            >
              {{ getAvatarInitials(campaign.accountManagerUserId) }}
            </div>
          </td>
          <td
            ref="row-details-actions"
            class="row-details-actions"
          >
            <CampaignActions
              :index="index"
              :item="campaign"
              @removeCampaign="removeCampaign"
              @startOrPauseCampaign="setCampaignLive"
              @setCampaignToDraft="setCampaignToDraft"
              @refreshTable="refreshTable"
              @setDormantToLive="setCampaignLive"
              :is-my-dashboard="isMyDashboard"
            />
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import CampaignActions from '@/modules/campaigns/components/CampaignActions';

import * as types from '@/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';

import dateHelper from '@/core/mixins/dateHelper';
import currenciesMixin from '@/core/mixins/currencies';
import tableMixin from '@/modules/campaigns/mixins/tableMixin';

import { mapActions, mapMutations, mapState } from 'vuex';

export default {
  name: 'CampaignsTable',
  mixins: [
    dateHelper,
    currenciesMixin,
    tableMixin,
  ],
  components: {
    CampaignActions,
  },
  props: {
    campaigns: {
      type: Array,
      required: true,
    },
    isMyDashboard: {
      type: Boolean,
      required: true,
    },
    accountManagersMap: {
      type: Map,
      required: true,
    },
    isManaged: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    ...mapState('campaigns', [
      'currencies',
    ]),
    dashboardName() {
      return this.isMyDashboard ? 'dashboard' : 'selfServiceDashboard';
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.REMOVE_CAMPAIGN,
      campaignTypes.SET_CAMPAIGN_STATE,
    ]),
    ...mapMutations([
      types.SET_GLOBAL_MESSAGE,
    ]),
    setCampaignToDraft(item) {
      this.isBusy = true;

      this.SET_CAMPAIGN_STATE({
        advertiserId: item.advertiserId,
        campaignId: item.id,
        state: 'DRAFT',
        isSelfServiceDashboard: !this.isMyDashboard,
      })
        .then(() => {
          this.campaigns.find((i) => i.id === item.id).state = 'DRAFT';
          this.isBusy = false;
          this.SET_GLOBAL_MESSAGE({
            message: 'Campaign was set to DRAFT.',
            type: 'info',
          });
        });
    },
    removeCampaign(item) {
      this.REMOVE_CAMPAIGN({
        advertiserId: item.advertiserId,
        campaignId: item.id,
        campaignName: item.name,
      })
        .finally(() => {
          this.refreshTable();
        }).catch(() => {
          this.refreshTable();
        });
    },
    refreshTable() {
      this.$emit('refreshTable');
    },
    setCampaignLive(item, state) {
      this.updateCampaignState(item, state);
    },
  },
};
</script>

<style lang="scss" scoped>
.intro-message {
  font-size: 1.5rem;
}
.full-width {
  width: 100% !important;
}
.custom-height-dropdown {
  height: 33px;
}

.row-details-expand {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-expand-width) + calc(0.75rem * 2));
}

.row-details-campaign-name {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-campaign-name-width)  + calc(0.75rem * 2));
}

.row-details-state {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-state-width)  + calc(0.75rem * 2));
}

.row-details-start-date {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-start-date-width) + calc(0.75rem * 2));
}

.row-details-end-date {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-end-date-width) + calc(0.75rem * 2));
}

.row-details-budget {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-budget-width)  + calc(0.75rem * 2));
}

.row-details-impressions {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-impressions-width) + calc(0.75rem * 2));
}

.row-details-clicks {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-clicks-width) + calc(0.75rem * 2));
}

.row-details-ctr {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-ctr-width) + calc(0.75rem * 2));
}

.row-details-account-manager {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-account-manager-width) + calc(0.75rem * 2));
}

.row-details-actions {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-actions-width) + calc(0.75rem * 2));
}
</style>
